import { LOCALES } from '@repo/shared-config/constants/locales';

export const MONEY_TOKENS = {
  ISO: 'money-iso', // 1234.56 -> "1,234.56 EUR"
  ISO_COMPACT: 'money-iso-compact', // 1234 -> "1,234 EUR"
  ISO_SIGN_COMPACT: 'money-iso-sign-compact', // 1234 -> "+1,234 EUR"
  ISO_SIGN_DECIMAL: 'money-iso-sign-decimal', // 1234.56 -> "+1,234.56 EUR"
  SYMBOL: 'money-symbol', // 1234.56 -> "€1,234.56"
  SYMBOL_COMPACT: 'money-symbol-compact', // 1234 -> "€1,234"
  SYMBOL_SIGN_COMPACT: 'money-symbol-sign-compact', // 1234 -> "+€1,234"
  SYMBOL_SIGN_DECIMAL: 'money-symbol-sign-decimal', // 1234.56 -> "+€1,234.56"
} as const;

export type MoneyToken = (typeof MONEY_TOKENS)[keyof typeof MONEY_TOKENS];

interface TokenFormatRules {
  useSymbol: boolean;
  sign: 'auto' | 'always';
  decimals: 'always' | 'if-needed';
}

const MONEY_TOKEN_RULES: Record<MoneyToken, TokenFormatRules> = {
  [MONEY_TOKENS.ISO]: { useSymbol: false, sign: 'auto', decimals: 'always' },
  [MONEY_TOKENS.ISO_COMPACT]: { useSymbol: false, sign: 'auto', decimals: 'if-needed' },
  [MONEY_TOKENS.ISO_SIGN_COMPACT]: { useSymbol: false, sign: 'always', decimals: 'if-needed' },
  [MONEY_TOKENS.ISO_SIGN_DECIMAL]: { useSymbol: false, sign: 'always', decimals: 'always' },
  [MONEY_TOKENS.SYMBOL]: { useSymbol: true, sign: 'auto', decimals: 'always' },
  [MONEY_TOKENS.SYMBOL_COMPACT]: { useSymbol: true, sign: 'auto', decimals: 'if-needed' },
  [MONEY_TOKENS.SYMBOL_SIGN_COMPACT]: { useSymbol: true, sign: 'always', decimals: 'if-needed' },
  [MONEY_TOKENS.SYMBOL_SIGN_DECIMAL]: { useSymbol: true, sign: 'always', decimals: 'always' },
};

type FormatterOverrideFn = (
  parts: Intl.NumberFormatPart[],
  context: { amount: number; useSymbol: boolean }
) => string;

/**
 * Custom formatting rules specific to Qonto's requirements.
 * These override the default Intl.NumberFormat behavior for certain locales
 * to ensure consistent currency formatting across the application.
 *
 * The FormatterOverrideFn allows us to manipulate the formatted parts
 * returned by Intl.NumberFormat to achieve specific formatting requirements
 * that aren't possible with the standard options.
 *
 * Examples include:
 * - Replacing spaces with dots for thousands separators in Portuguese
 * - Removing spaces between currency symbols and amounts for US English
 * - Controlling when to use grouping separators for Spanish
 */
const LOCALE_CUSTOM_FORMATS: Record<string, FormatterOverrideFn> = {
  'pt-PT': (parts, { amount }) => {
    return parts
      .map(({ type, value }) => {
        if (type === 'group') {
          return Math.abs(amount) >= 10000
            ? value.replace(/\s/g, '.') // replace space with dot
            : value.replace(/\s/g, ''); // remove space if < 10000
        }
        return value;
      })
      .join('');
  },
  'en-US': (parts, { useSymbol }) => {
    return parts
      .map(({ type, value }) => {
        if (useSymbol && type === 'literal') {
          return value.replace(/\s/g, '');
        }
        return value;
      })
      .join('');
  },
  'es-ES': parts => {
    const result: string[] = [];
    for (const part of parts) {
      if (part.type === 'integer') {
        // Inject dot as thousands separator
        const digits = part.value;
        const grouped = digits.replace(/\B(?=(?:\d{3})+(?!\d))/g, '.');
        result.push(grouped);
      } else {
        result.push(part.value);
      }
    }
    return result.join('');
  },
  'it-IT': parts => {
    const result: string[] = [];
    for (const part of parts) {
      if (part.type === 'integer') {
        // Inject dot as thousands separator
        const digits = part.value;
        const grouped = digits.replace(/\B(?=(?:\d{3})+(?!\d))/g, '.');
        result.push(grouped);
      } else {
        result.push(part.value);
      }
    }
    return result.join('');
  },
};

export interface FormatMoneyOptions {
  token?: MoneyToken;
  currency?: string;
  locale?: string;
  amount: number | string | null | undefined;
  overrides?: Intl.NumberFormatOptions;
  placeholder?: string;
}

/**
 * Formats a monetary amount according to specified rules.
 *
 * @param options - Formatting options object containing:
 * - amount: Amount to format
 * - locale: Locale for formatting
 * - currency: Currency code (default: 'EUR')
 * - token: Token defining the format style (default: 'money-iso')
 * - placeholder: String to return when amount is NaN (default: '--')
 * - overrides: Additional Intl.NumberFormat options
 *
 * @returns Formatted currency string
 *
 * @example
 * formatMoney(\{ amount: 1234.56, locale: 'fr-FR' \}) // "1 234,56 EUR"
 * formatMoney(\{ amount: 1234.00, locale: 'en-US', token: MONEY_TOKENS.SYMBOL_COMPACT \}) // "€1,234"
 * formatMoney(\{ amount: NaN, locale: 'en-US', placeholder: 'N/A' \}) // "N/A"
 */
export function formatMoney({
  amount,
  locale = 'en-US',
  currency = 'EUR',
  token = MONEY_TOKENS.ISO,
  placeholder = '--',
  overrides = {},
}: FormatMoneyOptions): string {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) {
    return placeholder;
  }

  const normalizedLocale = _normalizeLocale(locale);
  const { useSymbol, sign, decimals } = MONEY_TOKEN_RULES[token];
  const hasDecimalValue = numericAmount % 1 !== 0;
  const isZero = numericAmount === 0;

  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency,
    currencyDisplay: useSymbol ? 'symbol' : 'code',
    signDisplay: sign === 'always' && isZero ? 'exceptZero' : sign,
    ...(decimals === 'if-needed' && !hasDecimalValue
      ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      : {}),
    ...overrides,
  };

  const formatter = new Intl.NumberFormat(normalizedLocale, options);
  const customFormatter = LOCALE_CUSTOM_FORMATS[normalizedLocale];

  if (customFormatter) {
    const parts = formatter.formatToParts(numericAmount);
    return customFormatter(parts, { amount: numericAmount, useSymbol });
  }

  return formatter.format(numericAmount);
}

/**
 * Maps language codes to their regional locales.
 *
 * @example
 * _normalizeLocale('fr') // 'fr-FR'
 * _normalizeLocale('en') // 'en-US'
 */
function _normalizeLocale(code: string): string {
  const localeObj = LOCALES.find(locale => locale.code === code);
  return localeObj?.locale || code;
}
